import { useEffect, useId, useRef, type ReactNode } from 'react';
import { tx } from '@fsv/data';
import { Icon } from './Icon';
import './Sheet.css';

/**
 * Bottom sheet on phones, centred dialog from 768 px. Built on <dialog>
 * (showModal): focus is trapped, Escape closes, the page behind is inert.
 * Mount it to open it; unmount (or onClose) to close.
 */
export function Sheet({
  title,
  onClose,
  children,
  footer,
  wide = false,
  kicker,
  tour,
}: {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
  kicker?: ReactNode;
  /** Anchor of the demo tour (data-tour). */
  tour?: string;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (!dialog.open) dialog.showModal();
    const onCancel = (e: Event) => {
      e.preventDefault();
      closeRef.current();
    };
    dialog.addEventListener('cancel', onCancel);
    return () => {
      dialog.removeEventListener('cancel', onCancel);
      if (dialog.open) dialog.close();
    };
  }, []);

  return (
    <dialog
      ref={ref}
      className={`sheet${wide ? ' sheet--wide' : ''}`}
      aria-labelledby={titleId}
      data-tour={tour}
      onClick={(e) => {
        // A click on the backdrop lands on the dialog element itself.
        if (e.target === ref.current) onClose();
      }}
    >
      <div className="sheet__frame">
        <div className="sheet__grip" aria-hidden="true" />
        <header className="sheet__head">
          <div className="sheet__titles">
            {kicker && <div className="sheet__kicker">{kicker}</div>}
            <h2 id={titleId} className="sheet__title">
              {title}
            </h2>
          </div>
          <button type="button" className="btn btn--ghost btn--icon" onClick={onClose} aria-label={tx.app.close}>
            <Icon name="close" />
          </button>
        </header>
        <div className="sheet__body">{children}</div>
        {footer && <footer className="sheet__foot">{footer}</footer>}
      </div>
    </dialog>
  );
}
